import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { CTA } from "../components/CTA";
import { useReveal } from "../lib/hooks";
import { resolveImageUrl } from "../lib/adminApi";
import { ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JournalArticle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/journal/${id}`)
      .then((r) => mounted && setEntry(r.data))
      .catch(() => mounted && setNotFound(true))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <main className="bg-[var(--pc-bg)] pt-40 pb-32">
        <div className="pc-container label-small">Cargando nota...</div>
      </main>
    );
  }

  if (notFound || !entry) {
    return (
      <main className="bg-[var(--pc-bg)] pt-40 pb-32">
        <div className="pc-container">
          <h1 className="headline-lg text-2xl">Nota no encontrada.</h1>
          <div className="mt-10">
            <CTA testId="article-back-cta" to="/cuaderno">
              Volver al cuaderno
            </CTA>
          </div>
        </div>
      </main>
    );
  }

  return <ArticleView entry={entry} onBack={() => navigate("/cuaderno")} />;
}

function ArticleView({ entry, onBack }) {
  const meta = useReveal();
  const title = useReveal();
  const img = useReveal({ threshold: 0.05 });
  const intro = useReveal();

  const content = entry.content && entry.content.length
    ? entry.content
    : [entry.excerpt];

  return (
    <main data-testid="journal-article" className="bg-[var(--pc-bg)]">
      {/* Cover — full-bleed image with fade to bottom */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: "78vh" }}>
        <div
          ref={img.ref}
          className={`absolute inset-0 reveal-img ${img.revealed ? "is-in" : ""}`}
        >
          <img
            src={resolveImageUrl(entry.image)}
            alt={entry.title.replace("\n", " ")}
            className="absolute inset-0 w-full h-full object-cover"
            draggable="false"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(7,7,7,0.55) 0%, rgba(7,7,7,0.15) 28%, rgba(7,7,7,0.55) 78%, rgba(7,7,7,1) 100%)",
            }}
          />
        </div>

        <div
          className="relative z-10 pc-container flex flex-col justify-end"
          style={{ minHeight: "78vh", paddingTop: 160, paddingBottom: 64 }}
        >
          <button
            data-testid="article-back-link"
            onClick={onBack}
            className="inline-flex items-center gap-3 text-[var(--pc-text)] hover:text-[var(--pc-bronze-bright)] transition-colors mb-10 self-start"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 300,
              fontSize: 11.5,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
            }}
          >
            <ArrowLeft size={16} strokeWidth={1.2} />
            <span>Volver al cuaderno</span>
          </button>

          <div ref={meta.ref} className={`reveal ${meta.revealed ? "is-in" : ""}`}>
            <div
              className="text-[var(--pc-bronze)] mb-4"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: 11.5,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
              }}
            >
              {entry.number} · {formatDate(entry.date)}
            </div>
          </div>

          <div
            ref={title.ref}
            className={`reveal delay-1 ${title.revealed ? "is-in" : ""}`}
          >
            <h1
              className="headline-xl"
              style={{
                fontSize: "clamp(30px, 3.8vw, 56px)",
                letterSpacing: "0.14em",
                lineHeight: 1.16,
                maxWidth: 880,
                whiteSpace: "pre-line",
              }}
            >
              {entry.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="border-t border-[var(--pc-line)]">
        <div className="pc-container py-20 md:py-24 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3">
            <div
              className="label-small text-[var(--pc-text-soft)]"
              style={{ fontSize: 11 }}
            >
              Cuaderno · Paco Cabrera
            </div>
          </aside>

          <div className="lg:col-span-8 lg:col-start-5 max-w-[680px]">
            <div ref={intro.ref} className={`reveal ${intro.revealed ? "is-in" : ""}`}>
              <p
                className="text-[var(--pc-text)]"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 300,
                  fontSize: "clamp(18px, 1.4vw, 22px)",
                  lineHeight: 1.55,
                  letterSpacing: "0.005em",
                }}
              >
                {entry.excerpt}
              </p>
              <div className="h-px w-12 bg-[var(--pc-bronze)] my-10 opacity-90" />
            </div>

            <div className="space-y-7">
              {content.map((p, i) => (
                <p
                  key={i}
                  className="body-soft"
                  style={{ fontSize: 16.5, lineHeight: 1.78 }}
                >
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-16 pt-10 border-t border-[var(--pc-line)]">
              <CTA testId="article-cta" to="/contacto">
                Iniciemos una conversación privada
              </CTA>
            </div>

            <div className="mt-14">
              <Link
                to="/cuaderno"
                data-testid="article-back-bottom"
                className="nav-link inline-flex items-center gap-3"
              >
                <ArrowLeft size={14} strokeWidth={1.2} />
                <span>Más notas</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function formatDate(iso) {
  try {
    const [y, m, d] = iso.split("-");
    const months = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
    ];
    return `${parseInt(d, 10)} de ${months[parseInt(m, 10) - 1]} de ${y}`;
  } catch {
    return iso;
  }
}
